import React, { useState, useEffect } from 'react';
import { getJourneyPageData } from '../services/journeyService';
import { getStrapiMedia, renderSafeText } from '../lib/strapi';
import { Calendar, MapPin, Milestone, History } from 'lucide-react';

const Journey = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const result = await getJourneyPageData();
      setData(result);
      setLoading(false);
    };
    fetchData();
  }, []);

  if (loading || !data) {
    return (
      <div className="py-40 text-center flex flex-col items-center justify-center space-y-4">
        <div className="w-12 h-12 border-4 border-widya-green border-t-transparent rounded-full animate-spin"></div>
        <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px]">Loading Timeline...</p>
      </div>
    );
  }

  const heroImageUrl = getStrapiMedia(data.content?.hero_image);

  return (
    <div className="bg-white pb-24">
      {/* IMMERSIVE HERO SECTION */}
      <section className="relative overflow-hidden min-h-[60vh] lg:min-h-[85vh] flex items-center justify-center">
        {heroImageUrl ? (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${heroImageUrl})` }}
            />
            {/* Lighter overlay */}
            <div className="absolute inset-0 bg-black/40" />
          </>
        ) : (
          <div className="absolute inset-0 bg-widya-footer" />
        )}
        <div className="container mx-auto px-6 max-w-[1200px] text-center relative z-10 w-full mt-12">
          <h1 className="text-[46px] md:text-[64px] lg:text-[80px] font-bold tracking-tight leading-[1.1] max-w-5xl mx-auto font-heading text-white drop-shadow-lg mb-8">
            {renderSafeText(data.content?.hero_title)}
          </h1>
          <p className="text-[18px] md:text-[22px] text-[#F4D03F] max-w-5xl mx-auto font-medium leading-[1.6] drop-shadow-md px-4">
            {renderSafeText(data.content?.hero_intro_text)}
          </p>
        </div>
      </section>

      {/* SMALLHOLDER FARMERS SECTION (Missing Strapi Section) */}
      {(data.content?.problem_title || data.content?.problem_description) && (
        <section className="py-24 px-6 bg-white overflow-hidden">
          <div className="container mx-auto max-w-[1100px] grid lg:grid-cols-2 gap-x-12 gap-y-12 items-start">
            
            {/* Left Side: Title and Text */}
            <div className="flex flex-col">
              <h2 className="text-[34px] md:text-[40px] font-black text-black mb-8 font-heading tracking-[-1px] leading-[1.1]">
                {renderSafeText(data.content?.problem_title)}
              </h2>
              <div className="text-[16px] text-[#111111] leading-[1.7] whitespace-pre-wrap font-normal pr-4">
                {renderSafeText(data.content?.problem_description)}
              </div>
            </div>

            {/* Right Side: Image exactly flush right */}
            <div className="flex justify-end lg:justify-end items-start w-full">
              {data.content?.problem_image && (
                <img
                  src={getStrapiMedia(data.content.problem_image)}
                  alt={renderSafeText(data.content?.problem_title)}
                  className="w-full max-w-[500px] h-auto object-cover"
                />
              )}
            </div>

          </div>
        </section>
      )}

      {/* TIMELINE SECTION */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="container mx-auto max-w-[1100px] relative">
          
          {/* Approach Title above TIMELINE */}
          {data.content?.approach_title && (
            <div className="text-center mb-24">
              <h2 className="text-[32px] md:text-[36px] font-black text-widya-charcoal font-heading tracking-tight leading-[1.2]">
                {renderSafeText(data.content.approach_title)}
              </h2>
            </div>
          )}

          <div className="absolute left-4 md:left-1/2 top-[120px] bottom-0 w-0.5 bg-widya-green/20 -translate-x-1/2"></div>

          <div className="space-y-16 md:space-y-24">
            {data.projects?.map((item, index) => {
              // Strapi v5: flat data, no .attributes needed
              const project = item.attributes || item;
              const isEven = index % 2 === 0;

              return (
                <div key={item.id || index} className={`relative flex flex-col md:flex-row items-center ${isEven ? 'md:flex-row-reverse' : ''}`}>
                  <div className="absolute left-4 md:left-1/2 w-8 h-8 bg-widya-green rounded-full border-4 border-white shadow-md -translate-x-1/2 z-10 flex items-center justify-center">
                    <Milestone size={12} className="text-white" />
                  </div>

                  <div className={`ml-12 md:ml-0 md:w-1/2 ${isEven ? 'md:pl-16' : 'md:pr-16'}`}>
                    <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 group">
                      <div className={`flex items-center gap-2 text-widya-green font-extrabold mb-2 ${isEven ? '' : 'md:justify-end'}`}>
                        <Calendar size={16} />
                        <span>{renderSafeText(project.date_range || project.year)}</span>
                      </div>

                      <h3 className={`text-2xl font-bold text-widya-charcoal mb-2 font-heading ${isEven ? '' : 'md:text-right'}`}>
                        {renderSafeText(project.title)}
                      </h3>

                      <div className={`flex items-center gap-2 text-gray-400 text-xs uppercase tracking-widest mb-6 ${isEven ? '' : 'md:justify-end'}`}>
                        <MapPin size={14} />
                        <span>{renderSafeText(project.location)}</span>
                      </div>

                      <p className={`text-widya-body leading-relaxed text-sm mb-6 ${isEven ? '' : 'md:text-right'}`}>
                        {renderSafeText(project.description)}
                      </p>

                      {/* project_image is an ARRAY of images */}
                      {project.project_image && project.project_image.length > 0 && (
                        <div className="rounded-2xl overflow-hidden shadow-md group-hover:shadow-lg transition-shadow">
                          <img
                            src={getStrapiMedia(project.project_image[0])}
                            alt={renderSafeText(project.title)}
                            className="w-full h-48 md:h-64 object-cover"
                          />
                        </div>
                      )}

                    </div>
                  </div>

                  <div className="hidden md:block md:w-1/2"></div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Journey;